// Admin System Health — one-click confirm that LIVE + DEMO databases are seeded
// (especially right after a production deploy). Calls the public GET /api/health
// seed-count probe and renders a clear ready/degraded verdict per environment.
import { useCallback, useEffect, useState } from "react";
import { Gauge, RefreshCw, CheckCircle2, AlertTriangle, Database, Loader2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

const PROD_URL = "https://priorityrisk.beyond-ops.net";

function EnvCard({ env, label, counts, ready }) {
    const error = counts?.error;
    const tone = error ? "#B33A2E" : ready ? "#3F7D3A" : "#C68A1C";
    const bg = error ? "#FBE8E5" : ready ? "#E8F1E6" : "#FCF1DC";
    const rows = [
        ["Users", counts?.users],
        ["Clients", counts?.clients],
        ["Buildings", counts?.buildings],
        ["BSC records", counts?.bsc_records],
    ];
    return (
        <div data-testid={`health-env-${env}`} className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
            <div className="px-5 py-3.5 flex items-center justify-between border-b" style={{ borderColor: "#F0EDE8" }}>
                <div className="flex items-center gap-2">
                    <Database className="w-4 h-4" style={{ color: "#6F6A64" }} />
                    <h3 className="text-[14px] font-semibold text-[#1F1F1F]">{label}</h3>
                </div>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ background: bg, color: tone }}>
                    {error ? <AlertTriangle className="w-3.5 h-3.5" /> : ready ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                    {error ? "Error" : ready ? "Seeded" : "Not seeded"}
                </span>
            </div>
            {error ? (
                <div className="px-5 py-4 text-[12px]" style={{ color: "#B33A2E" }}>{error}</div>
            ) : (
                <div className="grid grid-cols-2 gap-px" style={{ background: "#F0EDE8" }}>
                    {rows.map(([k, v]) => (
                        <div key={k} className="bg-white px-5 py-3">
                            <div className="text-[22px] font-bold leading-none" style={{ color: (v || 0) > 0 ? "#1F1F1F" : "#C7C2BB" }}>{v ?? "—"}</div>
                            <div className="text-[10px] uppercase tracking-wider mt-1" style={{ color: "#9A968F" }}>{k}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function SystemHealthPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [checkedAt, setCheckedAt] = useState(null);
    const [failed, setFailed] = useState(false);

    const check = useCallback(async () => {
        setLoading(true);
        setFailed(false);
        try {
            const r = await api.get("/health", { timeout: 20000 });
            setData(r.data);
            setCheckedAt(new Date());
        } catch {
            setFailed(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { check(); }, [check]);

    const live = data?.modes?.live;
    const demo = data?.modes?.demo;
    const liveReady = !!live && !live.error && (live.users || 0) >= 1;
    const demoReady = !!demo && !demo.error && (demo.users || 0) >= 1;
    const allReady = liveReady && demoReady && data?.status === "ok";

    return (
        <div data-testid="system-health-page" className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                        style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>
                        Admin
                    </span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F] flex items-center gap-2">
                        <Gauge className="w-6 h-6" style={{ color: "var(--brand-accent, #C75A1B)" }} /> System Health
                    </h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        Confirm the LIVE and DEMO databases are seeded — run this right after a production deploy.
                    </p>
                </div>
                <button
                    data-testid="health-recheck-button"
                    onClick={check}
                    disabled={loading}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white transition-colors disabled:opacity-60"
                    style={{ background: "var(--brand-accent, #C75A1B)" }}
                >
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    {loading ? "Checking…" : "Recheck"}
                </button>
            </div>

            {/* Verdict banner */}
            {failed ? (
                <div data-testid="health-verdict" className="px-4 py-3 rounded-[11px] text-[13px] font-semibold flex items-center gap-2" style={{ background: "#FBE8E5", color: "#B33A2E", border: "1px solid #F2D0CA" }}>
                    <AlertTriangle className="w-4 h-4" /> Couldn't reach the health endpoint. The API may be starting up — wait a moment and Recheck.
                </div>
            ) : data ? (
                <div data-testid="health-verdict" className="px-4 py-3 rounded-[11px] text-[13px] font-semibold flex items-center gap-2"
                    style={allReady
                        ? { background: "#E8F1E6", color: "#3F7D3A", border: "1px solid #CFE4CB" }
                        : { background: "#FCF1DC", color: "#9A6B12", border: "1px solid #EAD9B0" }}>
                    {allReady ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                    {allReady
                        ? "Both environments are seeded and healthy — login picker will load."
                        : "One or more environments look unseeded or degraded — see below. After a fresh deploy, the demo sandbox fills in the background; Recheck in a few seconds."}
                    {checkedAt && <span className="ml-auto font-normal opacity-70 text-[11.5px]">Checked {checkedAt.toLocaleTimeString("en-GB")}</span>}
                </div>
            ) : null}

            {data && (
                <div className="grid md:grid-cols-2 gap-5">
                    <EnvCard env="live" label="LIVE (production data)" counts={live} ready={liveReady} />
                    <EnvCard env="demo" label="DEMO (sandbox)" counts={demo} ready={demoReady} />
                </div>
            )}

            <div className="bg-white border rounded-[11px] p-4 text-[12px] flex items-center gap-2" style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}>
                <span>Production:</span>
                <a href={PROD_URL} target="_blank" rel="noreferrer" className="font-semibold inline-flex items-center gap-1 hover:underline" style={{ color: "var(--brand-accent, #C75A1B)" }}>
                    {PROD_URL} <ExternalLink className="w-3 h-3" />
                </a>
                <span className="ml-auto">Probe: <code className="px-1.5 py-0.5 rounded bg-[#F6F4F0]">GET /api/health</code></span>
            </div>
        </div>
    );
}
